/**
 * Session Storage Utility
 * Manages AMA conversation session IDs for authenticated (logged-in) users.
 *
 * Scenario 1: Chat remembered through many sessions when logged-in (localStorage)
 * Scenario 2: Refreshes when user A <> user B transition detected
 * Scenario 4: Separate companies create separate sessions
 */
const trackingConversationIdKey = (companyId) => `amaTrackingConvId_${companyId}`;
/**
 * Fetches the verified candidate ID from the server and caches it in
 * localStorage so getCurrentSessionId can use it synchronously.
 */
export async function resolveCandidateId(companyId) {
    try {
        const response = await fetch(`/hub/${companyId}/api/ama/me`);
        const data = await response.json();
        const candidateId = data.candidate_id;
        localStorage.setItem(`resolvedCandidateId_${companyId}`, candidateId);
        return candidateId;
    }
    catch (error) {
        console.error('[SessionStorage] Error resolving candidate ID:', error);
        return localStorage.getItem(`resolvedCandidateId_${companyId}`) ?? '';
    }
}
/**
 * Resolves the candidate ID, detects user transitions (user A → user B),
 * and clears stale sessions. Returns the current session ID, or null if
 * this is a fresh session (first message — backend will assign one).
 *
 * Call this on page load before any sendMessage call.
 *
 * @param companyId - The company ID
 * @returns The existing session ID, or null for a fresh session
 */
export async function initSessionForUser(companyId) {
    // Use the cached candidate ID if available (populated by resolveCandidateId on
    // page load). Only fetch from the server when the cache is empty.
    const resolvedId = localStorage.getItem(`resolvedCandidateId_${companyId}`) ??
        (await resolveCandidateId(companyId));
    const lastAuthStateKey = `lastAuthState_${companyId}`;
    const lastCandidateId = localStorage.getItem(lastAuthStateKey);
    // Detect transition: user changed (Scenario 2)
    const isTransition = lastCandidateId !== null && lastCandidateId !== resolvedId;
    if (isTransition) {
        console.log(`[SessionStorage] Auth transition detected for ${companyId}: ${lastCandidateId} -> ${resolvedId}`);
        // Clear the stale session — next message will start a fresh one via backend
        localStorage.removeItem(`session_${lastCandidateId}_${companyId}`);
        // The previous user's tracking conversation id must not leak into the new
        // user's analytics events.
        clearTrackingConversationId(companyId);
    }
    localStorage.setItem(lastAuthStateKey, resolvedId);
    const sessionId = localStorage.getItem(`session_${resolvedId}_${companyId}`);
    if (sessionId) {
        console.log(`[SessionStorage] Restored session for ${resolvedId} at ${companyId}`);
    }
    return sessionId;
}
/**
 * Updates the stored session ID with the one returned by the Kinetic API.
 * Called after a successful send-question response to ensure the client
 * always tracks the session UUID that the API actually assigned.
 */
export function updateSessionId(companyId, newSessionId) {
    const candidateId = localStorage.getItem(`resolvedCandidateId_${companyId}`) || 'unknown';
    const sessionKey = `session_${candidateId}_${companyId}`;
    localStorage.setItem(sessionKey, newSessionId);
}
/**
 * Clears the session ID for the current user and company.
 * Used when the user explicitly wants to start a new conversation.
 *
 * @param config - Configuration with candidateId and companyId
 */
export function clearSessionId(config) {
    const { companyId } = config;
    const candidateId = localStorage.getItem(`resolvedCandidateId_${companyId}`) || 'unknown';
    const sessionKey = `session_${candidateId}_${companyId}`;
    localStorage.removeItem(sessionKey);
    // Tracking id is bound to the BE session lifecycle — clearing one clears the other
    // so the next conversation gets a fresh `amaConversationID`.
    clearTrackingConversationId(companyId);
    console.log(`[SessionStorage] Cleared session for ${candidateId} at ${companyId}`);
}
/**
 * Gets the current session ID without creating a new one.
 * Returns null if no session exists.
 *
 * Uses the cached resolved candidate ID (set by a previous async call to
 * getOrCreateSessionId or resolveCandidateId) for correct key lookup.
 *
 * @param config - Configuration with candidateId and companyId
 * @returns Session ID or null
 */
export function getCurrentSessionId(config) {
    const { companyId } = config;
    const candidateId = localStorage.getItem(`resolvedCandidateId_${companyId}`) || 'unknown';
    const sessionKey = `session_${candidateId}_${companyId}`;
    return localStorage.getItem(sessionKey);
}
/**
 * Returns a stable, client-generated conversation id used solely for product
 * analytics (`amaConversationID` in tracking events, spec §1.2). Decoupled from
 * the BE session id because that one is only known after the first response —
 * tracking needs an id on the very first interaction.
 *
 * The id is regenerated whenever the BE session is cleared (see clearSessionId).
 */
export function getOrCreateTrackingConversationId(companyId) {
    const key = trackingConversationIdKey(companyId);
    const existing = localStorage.getItem(key);
    if (existing)
        return existing;
    const fresh = `ama-${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    localStorage.setItem(key, fresh);
    return fresh;
}
export function clearTrackingConversationId(companyId) {
    localStorage.removeItem(trackingConversationIdKey(companyId));
}
